import { BetTypeKey } from '../../models/bet-type.model';
import { OddKey } from '../../models/odd.model';
import { Sport } from '../../models/sport.model';
import { QuickSelectorDefinition, QuickSelectorGroupId, SportQuickSelectorPreset } from './sportsbook-config.types';

export const QUICK_SELECTOR_DEFINITIONS = {
  result: {
    id: 'result',
    candidateBetTypeKeys: [
      BetTypeKey.REST_OF_MATCH,
      BetTypeKey.REST_OF_MATCH_OT,
      BetTypeKey.WAY_3,
      BetTypeKey.WAY_2,
      BetTypeKey.TENNIS_MATCH_WINNER
    ],
    columns: [
      {
        id: 'home',
        defaultHeader: '1',
        oddKeys: [OddKey.ROM_1, OddKey.ROM_OT_1, OddKey.RESULT_1, OddKey.RESULT_OT_1, OddKey.WAY2_1, OddKey.TENNIS_MW_1],
        selectionResolver: 'home'
      },
      {
        id: 'draw',
        defaultHeader: 'X',
        oddKeys: [OddKey.ROM_X, OddKey.ROM_OT_X, OddKey.RESULT_X, OddKey.RESULT_OT_X],
        selectionResolver: 'draw'
      },
      {
        id: 'away',
        defaultHeader: '2',
        oddKeys: [OddKey.ROM_2, OddKey.ROM_OT_2, OddKey.RESULT_2, OddKey.RESULT_OT_2, OddKey.WAY2_2, OddKey.TENNIS_MW_2],
        selectionResolver: 'away'
      }
    ]
  },
  handicap: {
    id: 'handicap',
    candidateBetTypeKeys: [BetTypeKey.HANDICAP],
    columns: [
      {
        id: 'home',
        defaultHeader: '1',
        oddKeys: [OddKey.HC_1],
        selectionResolver: 'home'
      },
      {
        id: 'away',
        defaultHeader: '2',
        oddKeys: [OddKey.HC_2],
        selectionResolver: 'away'
      }
    ]
  },
  totals: {
    id: 'totals',
    candidateBetTypeKeys: [
      BetTypeKey.UNDER_OVER,
      BetTypeKey.UNDER_OVER_OT,
      BetTypeKey.TENNIS_UNDER_OVER,
      BetTypeKey.TENNIS_SET_UNDER_OVER
    ],
    columns: [
      {
        id: 'over',
        defaultHeader: 'OVER',
        oddKeys: [OddKey.UO_O, OddKey.UO_OT_O, OddKey.TENNIS_UO_O, OddKey.TENNIS_SET_UO_O],
        selectionResolver: 'over'
      },
      {
        id: 'under',
        defaultHeader: 'UNDER',
        oddKeys: [OddKey.UO_U, OddKey.UO_OT_U, OddKey.TENNIS_UO_U, OddKey.TENNIS_SET_UO_U],
        selectionResolver: 'under'
      }
    ]
  },
  setWinner: {
    id: 'setWinner',
    candidateBetTypeKeys: [BetTypeKey.TENNIS_SET_WINNER],
    columns: [
      {
        id: 'home',
        defaultHeader: '1',
        oddKeys: [OddKey.TENNIS_SW_1],
        selectionResolver: 'home'
      },
      {
        id: 'away',
        defaultHeader: '2',
        oddKeys: [OddKey.TENNIS_SW_2],
        selectionResolver: 'away'
      }
    ]
  },
  ggng: {
    id: 'ggng',
    candidateBetTypeKeys: [BetTypeKey.GOAL_NOGOAL],
    columns: [
      {
        id: 'goal',
        defaultHeader: 'GG',
        oddKeys: [OddKey.GNG_GOAL],
        selectionResolver: 'yes'
      },
      {
        id: 'nogoal',
        defaultHeader: 'NG',
        oddKeys: [OddKey.GNG_NOGOAL],
        selectionResolver: 'no'
      }
    ]
  }
} as const satisfies Record<QuickSelectorGroupId, QuickSelectorDefinition>;

const SOCCER_PRESETS = [
  { groupId: 'result', marketLabel: 'Match Result', marketLabelKey: 'sportsbook.quickMarkets.matchResult' },
  { groupId: 'totals', preferredSpecialValue: 2.5, marketLabel: 'Total Goals', marketLabelKey: 'sportsbook.quickMarkets.totalGoals' },
  { groupId: 'ggng', marketLabel: 'Both Teams To Score', marketLabelKey: 'sportsbook.quickMarkets.bothTeamsToScore' }
] as const satisfies readonly SportQuickSelectorPreset[];

const ICE_HOCKEY_PRESETS = [
  { groupId: 'result', marketLabel: 'Match Result', marketLabelKey: 'sportsbook.quickMarkets.matchResult' },
  { groupId: 'totals', marketLabel: 'Total Goals', marketLabelKey: 'sportsbook.quickMarkets.totalGoals' },
  { groupId: 'ggng', marketLabel: 'Both Teams To Score', marketLabelKey: 'sportsbook.quickMarkets.bothTeamsToScore' }
] as const satisfies readonly SportQuickSelectorPreset[];

const BASKETBALL_PRESETS = [
  { groupId: 'result', marketLabel: 'Match Result', marketLabelKey: 'sportsbook.quickMarkets.matchResult' },
  { groupId: 'handicap', marketLabel: 'Handicap', marketLabelKey: 'sportsbook.quickMarkets.handicap', visibility: 'live' },
  { groupId: 'totals', marketLabel: 'Total Points', marketLabelKey: 'sportsbook.quickMarkets.totalPoints' }
] as const satisfies readonly SportQuickSelectorPreset[];

const BASEBALL_PRESETS = [
  { groupId: 'result', marketLabel: 'Match Result', marketLabelKey: 'sportsbook.quickMarkets.matchResult' },
  { groupId: 'totals', marketLabel: 'Total Runs', marketLabelKey: 'sportsbook.quickMarkets.totalRuns' }
] as const satisfies readonly SportQuickSelectorPreset[];

const TENNIS_PRESETS = [
  { groupId: 'result', marketLabel: 'Match Winner', marketLabelKey: 'sportsbook.quickMarkets.matchWinner' },
  { groupId: 'setWinner', marketLabel: 'Set Winner', marketLabelKey: 'sportsbook.quickMarkets.setWinner' },
  { groupId: 'totals', marketLabel: 'Total Games', marketLabelKey: 'sportsbook.quickMarkets.totalGames' }
] as const satisfies readonly SportQuickSelectorPreset[];

const DART_PRESETS = [
  { groupId: 'result', marketLabel: 'Result', marketLabelKey: 'sportsbook.quickMarkets.result' },
  { groupId: 'setWinner', marketLabel: 'Set Winner', marketLabelKey: 'sportsbook.quickMarkets.setWinner' }
] as const satisfies readonly SportQuickSelectorPreset[];

const RESULT_AND_TOTALS_PRESETS = [
  { groupId: 'result', marketLabel: 'Match Result', marketLabelKey: 'sportsbook.quickMarkets.matchResult' },
  { groupId: 'totals', marketLabel: 'Total Goals', marketLabelKey: 'sportsbook.quickMarkets.totalGoals' }
] as const satisfies readonly SportQuickSelectorPreset[];

const RESULT_ONLY_PRESETS = [
  { groupId: 'result', marketLabel: 'Match Result', marketLabelKey: 'sportsbook.quickMarkets.matchResult' }
] as const satisfies readonly SportQuickSelectorPreset[];

const SPORT_QUICK_SELECTOR_PRESETS: Partial<Record<Sport, readonly SportQuickSelectorPreset[]>> = {
  [Sport.SOCCER]: SOCCER_PRESETS,
  [Sport.BASKETBALL]: BASKETBALL_PRESETS,
  [Sport.BASEBALL]: BASEBALL_PRESETS,
  [Sport.ICE_HOCKEY]: ICE_HOCKEY_PRESETS,
  [Sport.TENNIS]: TENNIS_PRESETS,
  [Sport.HANDBALL]: RESULT_AND_TOTALS_PRESETS,
  [Sport.MOTORSPORT]: RESULT_ONLY_PRESETS,
  [Sport.RUGBY]: RESULT_AND_TOTALS_PRESETS,
  [Sport.WINTERSPORT]: RESULT_ONLY_PRESETS,
  [Sport.FOOTBALL]: RESULT_AND_TOTALS_PRESETS,
  [Sport.TABLE_TENNIS]: RESULT_AND_TOTALS_PRESETS,
  [Sport.VOLLEYBALL]: RESULT_AND_TOTALS_PRESETS,
  [Sport.BEACHVOLLEYBALL]: RESULT_AND_TOTALS_PRESETS,
  [Sport.DART]: DART_PRESETS,
  [Sport.E_SOCCER]: SOCCER_PRESETS,
  [Sport.E_BASKETBALL]: BASKETBALL_PRESETS,
  [Sport.E_ICE_HOCKEY]: ICE_HOCKEY_PRESETS
};

export function getQuickSelectorPreset(sportId: number, isLive = false): readonly SportQuickSelectorPreset[] {
  return (SPORT_QUICK_SELECTOR_PRESETS[sportId as Sport] ?? []).filter(preset => {
    if (!preset.visibility || preset.visibility === 'all') {
      return true;
    }

    return preset.visibility === 'live' ? isLive : !isLive;
  });
}
